/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React from "react"
import classnames from "classnames"
import { Alert, Button } from "@duxton-web/ui"
import { useTranslations } from "common/utils/i18n"
import { useIsBcrsEnabled } from "pages/menu/detail/hooks/useIsBcrsEnabled"
import styles from "./BcrsNudgeBanner.modules.css"

interface BcrsNudgeBannerProps {
  onDownloadSuggestions: () => void
  isDownloading?: boolean
  className?: string
}

/**
 * "Review your BCRS items" nudge banner with a "Download BCRS suggestions"
 * CTA. Renders only when the per-merchant BCRS flag is enabled. Reused on the
 * menu overview and inside the bulk-upload download drawer.
 */
export const BcrsNudgeBanner = ({
  onDownloadSuggestions,
  isDownloading,
  className,
}: BcrsNudgeBannerProps) => {
  const isBcrsEnabled = useIsBcrsEnabled()
  const [nudgeTitle, nudgeBody, downloadSuggestionsText] = useTranslations([
    "menu.bcrs.bulk.nudgeTitle",
    "menu.bcrs.bulk.nudgeBody",
    "menu.bcrs.bulk.downloadSuggestions",
  ])

  if (!isBcrsEnabled) {
    return null
  }

  return (
    <Alert
      className={classnames(styles.banner, className)}
      type="warning"
      message={nudgeTitle}
      description={
        <div>
          <div>{nudgeBody}</div>
          <Button
            type="link"
            size="small"
            className={styles.downloadLink}
            loading={isDownloading}
            onClick={onDownloadSuggestions}
          >
            {downloadSuggestionsText}
          </Button>
        </div>
      }
    />
  )
}
